import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { loadCommitItems, loadGitHistory, versionFor } from '../../services/git/gitHistory';
import { pairWrites, pairFile } from '../../services/git/renamePairs';
import GitChangeRow from './GitChangeRow';
import GitPutBackButton from './GitPutBackButton';
import { gitWords } from '../../services/git/gitSyncStatus';
import CommitTaskChips, { useKnownTaskPrefixes } from './CommitTaskChips';

/** "Today 14:02" / "Yesterday 09:15" / "28 Sep 2026, 17:40" — like a mail client's list. */
export function whenLabel(seconds, now = new Date()) {
  const d = new Date(seconds * 1000);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  if (diff === 0) return `Today ${time}`;
  if (diff === 1) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}, ${time}`;
}

/** "Samin Grahich" → "SG", "GrahichDEV" → "G": the author's mark beside their name. */
export function initials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return (words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0][0]).toUpperCase();
}

/** Items read per page of one sync (owner, 05.10.2026: a 1,400-file first sync sat on "Loading…" while every file was read). */
export const COMMIT_PAGE = 50;

/** The app's spinner (as in the DB Manager) with a short label. */
function Busy({ children }) {
  return (
    <p className="git-empty git-loading" role="status">
      <span className="dbm-spinner" aria-hidden /> {children}
    </p>
  );
}

function CommitRow({ workspace, commit, onPutBack, busy, loadItems, canPutBack, taskPrefixes = null }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // { items, files, read } | { error }
  const [more, setMore] = useState(false);
  // Sorted by path, so a page holds one folder's items together (documents, then boards…).
  const all = useMemo(() => [...(commit.files || [])].sort((a, b) => a.path.localeCompare(b.path)), [commit.files]);

  /** Read the next page of files (only those) and add their items. A small sync is read whole — renames pair across it. */
  const readFrom = async (from, prev) => {
    const size = all.length <= COMMIT_PAGE ? all.length : COMMIT_PAGE;
    const page = await loadItems(workspace, { ...commit, files: all.slice(from, from + size) });
    return {
      items: [...(prev?.items || []), ...(page.items || [])],
      files: [...(prev?.files || []), ...(page.files || [])],
      read: from + size,
    };
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !state) {
      try {
        setState(await readFrom(0, null));
      } catch (e) {
        setState({ error: e?.message || String(e) });
      }
    }
  };

  const showMore = async () => {
    setMore(true);
    try {
      setState(await readFrom(state.read, state));
    } catch (e) {
      setState((s) => ({ ...s, moreError: e?.message || String(e) }));
    } finally {
      setMore(false);
    }
  };
  const filesByPath = new Map((state?.files || []).map((f) => [f.path, f]));
  const left = state?.read != null ? all.length - state.read : 0;

  return (
    <div className="git-commit" data-testid="git-commit">
      <button type="button" className="git-commit-head" aria-expanded={open} onClick={toggle}>
        <ChevronRight size={13} className={`git-chevron${open ? ' git-chevron--open' : ''}`} />
        <span className="git-commit-when">{whenLabel(commit.time)}</span>
        <span className="git-commit-what">{commit.summary || commit.message}</span>
        {commit.author && (
          <span className="git-commit-who">
            <span className="git-av" aria-hidden>
              {initials(commit.author)}
            </span>
            {commit.author}
          </span>
        )}
      </button>
      {/* Task keys in the message (commit ↔ task links): chips that open the task. Outside the row's button — a button cannot hold buttons. */}
      {taskPrefixes && <CommitTaskChips text={commit.message} prefixes={taskPrefixes} className="git-commit-tasks" />}
      {open && (
        <div className="git-commit-body">
          {!state && <Busy>Loading what this sync changed…</Busy>}
          {state?.error && <p className="git-row-error">{state.error}</p>}
          {state?.items?.map((item) => (
            <GitChangeRow
              key={item.id}
              item={item}
              file={pairFile(item, filesByPath)}
              actions={onPutBack && canPutBack(item, 'restore') && (
                <GitPutBackButton
                  item={item}
                  mode="restore"
                  words={gitWords(workspace)}
                  disabled={busy}
                  onConfirm={() => {
                    const writes = pairWrites(item, filesByPath, 'restore', versionFor);
                    return onPutBack(workspace, item.paths ? writes : item.path, writes[0].content, 'restore');
                  }}
                />
              )}
            />
          ))}
          {left > 0 && (
            <div className="git-commit-more">
              <span className="git-row-sub">
                {state.read} of {all.length} shown
              </span>
              <button type="button" className="git-btn git-btn--ghost git-btn--small" onClick={showMore} disabled={more}>
                {more ? <span className="dbm-spinner" aria-hidden /> : null}
                Show {Math.min(COMMIT_PAGE, left)} more
              </button>
            </div>
          )}
          {state?.moreError && <p className="git-row-error">{state.moreError}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * History (git sync Stage 1, card 7 set 7c): the syncs of one workspace,
 * newest first; open one to see what it changed, item by item, and put an
 * item back to that version.
 */
export default function GitHistory({
  workspace,
  onPutBack,
  syncing,
  // A scope's repository (boards & documents) reads its own (scopeChanges.js).
  loadHistory = loadGitHistory,
  loadItems = loadCommitItems,
  canPutBack = () => true,
  // The detail card's History tab shows how many syncs there are.
  onCount = null,
}) {
  const [state, setState] = useState({ loading: true, commits: [], error: null });
  // Board key prefixes, for the task chips on each commit (commit ↔ task links).
  const taskPrefixes = useKnownTaskPrefixes();
  useEffect(() => {
    if (!state.loading && !state.error) onCount?.(state.commits.length);
  }, [state, onCount]);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      setState({ loading: false, commits: await loadHistory(workspace), error: null });
    } catch (e) {
      setState({ loading: false, commits: [], error: e?.message || String(e) });
    }
  }, [workspace, loadHistory]);

  useEffect(() => {
    if (!syncing) load();
  }, [load, syncing]);

  return (
    <div className="git-history" data-testid="git-history">
      {state.loading && (
        <div className="git-pad">
          <Busy>Loading the history…</Busy>
        </div>
      )}
      {state.error && <p className="git-row-error git-pad">{state.error}</p>}
      {!state.loading && !state.error && state.commits.length === 0 && (
        <p className="git-empty git-pad">{gitWords(workspace).noHistory}</p>
      )}
      {state.commits.map((c) => (
        <CommitRow key={c.oid} workspace={workspace} commit={c} onPutBack={onPutBack} busy={syncing} loadItems={loadItems} canPutBack={canPutBack} taskPrefixes={taskPrefixes} />
      ))}
    </div>
  );
}
